import { useEffect } from 'react';

/**
 * Reports this document's height to the parent window when it is embedded in an
 * iframe, so the host page can size the frame to its content.
 *
 * Iframes do not auto-size: without this every embed needs a hardcoded pixel
 * height, which means a standings table is either clipped or trailed by dead
 * whitespace on someone else's site. public/embed.js listens for the messages
 * posted here and resizes the frame it created.
 *
 * Renders nothing and no-ops entirely when the page is not framed, so it is
 * safe to mount unconditionally.
 */
const WidgetAutoResize = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Not embedded: nothing to report to.
    if (window.parent === window) return;

    let lastHeight = 0;

    const report = () => {
      const height = Math.ceil(
        Math.max(
          document.documentElement?.scrollHeight ?? 0,
          document.body?.scrollHeight ?? 0,
        ),
      );

      // Sub-pixel and single-pixel churn would post a message on every animation
      // frame and make the host page jitter.
      if (!height || Math.abs(height - lastHeight) < 2) return;
      lastHeight = height;

      // targetOrigin '*' is deliberate: an embed is installed on an arbitrary
      // customer domain that we cannot enumerate. The payload carries no user
      // data, only a height, so there is nothing to leak to a listener.
      window.parent.postMessage(
        { type: 'statharbour:resize', height, path: window.location.pathname },
        '*',
      );
    };

    report();

    // Content arrives asynchronously (league data, logos, fonts), so watch for
    // layout changes rather than measuring once on mount.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(report) : null;
    if (observer && document.documentElement) observer.observe(document.documentElement);

    window.addEventListener('load', report);
    const interval = window.setInterval(report, 1000);

    return () => {
      observer?.disconnect();
      window.removeEventListener('load', report);
      window.clearInterval(interval);
    };
  }, []);

  return null;
};

export default WidgetAutoResize;
